import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, IconButton, Paper, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer';
import { waWallpaperSx } from '@common/components/wa-wallpaper/wa-wallpaper';
import RowStack from "@/common/components/common/row-stack/RowStack";

const AiAssistant = React.memo(() => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Memoize close handler
  const handleClose = useCallback(() => {
    dispatch(btLayoutSidebarActions.closeSidebar());
  }, [dispatch]);

  // Memoize paper styles. The wallpaper is themed by CSS (see wa-wallpaper.ts):
  // it used to key on `user.isDarkMode`, the WEB APP's preference, so inside
  // WhatsApp it followed the wrong app's setting.
  const paperStyles = useMemo(() => ({
    width: '100%',
    height: '100%',
    ...waWallpaperSx(),
    backgroundSize: '412px 749px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }), []);

  return (
    <Paper elevation={0} sx={paperStyles}>
      <Box sx={{ backgroundColor: 'var(--color-surface)' }}>
        <RowStack alignItems="center" sx={{ justifyContent: 'space-between', px: 2 }}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              py: '11px',
              color: 'var(--color-text)',
              flex: 1,
              textAlign: 'center'
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: 400 }}>{t('aiAssistantPanel.title')}</span>
            <br />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>{t('aiAssistantPanel.chatHelper')}</span>
          </Typography>
          
          <Box sx={{ width: '40px' }} />
        </RowStack>
        <Divider />
      </Box>

      {/* AI Assistant Content */}
      <Box 
        sx={{ 
          flex: 1, 
          p: 3, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text)'
        }}
      >
        <Box 
          sx={{ 
            backgroundColor: 'var(--color-surface)',
            borderRadius: 2,
            p: 3,
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            🤖 AI Assistant
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, color: 'var(--color-text-secondary)' }}>
            Your intelligent chat companion is here to help you with:
          </Typography>

          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 📝 Message suggestions and templates
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 🌐 Language translation
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 📊 Chat analytics and insights
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • ⚡ Quick responses and automation
            </Typography>
            <Typography variant="body2">
              • 🎯 Smart scheduling recommendations
            </Typography>
          </Box>

          <Typography 
            variant="body2" 
            sx={{ 
              fontStyle: 'italic', 
              color: 'var(--color-text-secondary)',
              fontSize: '12px'
            }}
          >
            Coming soon! This feature is under development.
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
});

AiAssistant.displayName = 'AiAssistant';

export default AiAssistant;
